import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardSection, Button } from '@trafiflow/shared-ui'
import { agentplatService } from '../../../services/agentplat'
import { Sparkles, Loader2, CheckCircle2, AlertCircle, User, Upload, X, FileText, Rocket } from 'lucide-react'
import { ROUTES } from '../../../shared/config/routes'

interface UploadedFile {
    file: File
    key: string
    filename: string
    uploading: boolean
    error?: string
}

export default function AgentBuilder() {
    const navigate = useNavigate()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        avatar: '',
        systemInstructions: '',
    })
    const [agentId, setAgentId] = useState<string | null>(null)
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isPublishing, setIsPublishing] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState(false)
    const [publishSuccess, setPublishSuccess] = useState(false)

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
        if (error) setError(null)
    }

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (!files || files.length === 0) return

        // Need agentId to upload files
        if (!agentId) {
            setError('Please save the agent draft first before uploading files')
            return
        }

        for (let i = 0; i < files.length; i++) {
            const file = files[i]
            const uploadedFile: UploadedFile = {
                file,
                key: '',
                filename: file.name,
                uploading: true,
            }

            setUploadedFiles(prev => [...prev, uploadedFile])

            try {
                const result = await agentplatService.uploadAgentFile(agentId, file)
                setUploadedFiles(prev =>
                    prev.map(f =>
                        f.filename === file.name
                            ? { ...f, key: result.fileKey, uploading: false }
                            : f
                    )
                )
            } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string }
                console.error('Error uploading file:', err)
                setUploadedFiles(prev =>
                    prev.map(f =>
                        f.filename === file.name
                            ? { ...f, uploading: false, error: err.message || 'Upload failed' }
                            : f
                    )
                )
            }
        }

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const handleRemoveFile = (filename: string) => {
        setUploadedFiles(prev => prev.filter(f => f.filename !== filename))
    }

    const handleSaveDraft = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)
        setSuccess(false)

        // Validation
        if (!formData.name.trim()) {
            setError('Agent name is required')
            return
        }
        if (!formData.description.trim()) {
            setError('Agent description is required')
            return
        }

        setIsSubmitting(true)

        try {
            const result = await agentplatService.saveAgentDraft({
                name: formData.name.trim(),
                description: formData.description.trim(),
                avatar: formData.avatar.trim() || undefined,
                systemInstructions: formData.systemInstructions.trim() || undefined,
                agentId: agentId || undefined,
            })

            const agent = result.agent as { agentId?: string; id?: string } | undefined
            setAgentId(agent?.agentId || agent?.id || '')
            setSuccess(true)

            // Clear success message after 3 seconds
            setTimeout(() => {
                setSuccess(false)
            }, 3000)
        } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string }
            console.error('Error saving agent draft:', err)
            setError(err.message || 'Failed to save agent draft. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    const handlePublish = async () => {
        if (!agentId) {
            setError('Please save the agent draft first')
            return
        }

        setError(null)
        setIsPublishing(true)

        try {
            await agentplatService.publishAgent(agentId)
            setPublishSuccess(true)

            // Redirect to My Agents page after 2 seconds
            setTimeout(() => {
                navigate(ROUTES.AGENTS.MY_AGENTS)
            }, 2000)
        } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string }
            console.error('Error publishing agent:', err)
            setError(err.message || 'Failed to publish agent. Please try again.')
        } finally {
            setIsPublishing(false)
        }
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-tf-text">Agent Builder</h1>
                    <p className="text-tf-text-muted">Create your own custom AI agents. Define their personality, knowledge, and specific tools for your business.</p>
                </div>
            </div>

            <Card className="max-w-2xl">
                <CardSection>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Sparkles className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-tf-text">Create New Agent</h2>
                            <p className="text-sm text-tf-text-muted">Complete the form to create a new AI agent draft</p>
                        </div>
                    </div>

                    {success && (
                        <div className="mb-6 p-4 bg-tf-feedback-success-bg border border-green-200 rounded-lg flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                            <p className="text-sm font-medium text-green-800">Draft saved successfully!</p>
                        </div>
                    )}

                    {publishSuccess && (
                        <div className="mb-6 p-4 bg-tf-feedback-success-bg border border-green-200 rounded-lg flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-medium text-green-800">Agent published successfully!</p>
                                <p className="text-xs text-green-600 mt-1">Redirecting to agents list...</p>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="mb-6 p-4 bg-tf-feedback-error-bg border border-red-200 rounded-lg flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                            <p className="text-sm font-medium text-red-800">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSaveDraft} className="space-y-5">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-[#202223] mb-1.5">
                                Agent Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-[#e1e3e5] rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                                placeholder="e.g. Sales Assistant"
                                disabled={isSubmitting || isPublishing}
                                required
                            />
                            <p className="mt-1 text-xs text-tf-text-muted">A descriptive name for your agent</p>
                        </div>

                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-[#202223] mb-1.5">
                                Description <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={4}
                                className="w-full px-3 py-2 border border-[#e1e3e5] rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                                placeholder="Describe your agent's capabilities, personality, and purpose..."
                                disabled={isSubmitting || isPublishing}
                                required
                            />
                            <p className="mt-1 text-xs text-tf-text-muted">Explain what your agent does and how it can help</p>
                        </div>

                        <div>
                            <label htmlFor="systemInstructions" className="block text-sm font-medium text-[#202223] mb-1.5">
                                System Instructions
                            </label>
                            <textarea
                                id="systemInstructions"
                                name="systemInstructions"
                                value={formData.systemInstructions}
                                onChange={handleChange}
                                rows={6}
                                className="w-full px-3 py-2 border border-[#e1e3e5] rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none font-mono text-sm"
                                placeholder="Enter system instructions for your agent. These define the agent's behavior, personality, and how it should respond..."
                                disabled={isSubmitting || isPublishing}
                            />
                            <p className="mt-1 text-xs text-tf-text-muted">Define how your agent should behave and respond to users</p>
                        </div>

                        <div>
                            <label htmlFor="avatar" className="block text-sm font-medium text-[#202223] mb-1.5">
                                Avatar URL (Optional)
                            </label>
                            <input
                                type="url"
                                id="avatar"
                                name="avatar"
                                value={formData.avatar}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-[#e1e3e5] rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                                placeholder="https://example.com/image.png"
                                disabled={isSubmitting || isPublishing}
                            />
                            <p className="mt-1 text-xs text-tf-text-muted">URL of an image to represent your agent</p>
                        </div>

                        {/* File Upload Section */}
                        {agentId && (
                            <div>
                                <label className="block text-sm font-medium text-[#202223] mb-1.5">
                                    Agent Files
                                </label>
                                <div className="border-2 border-dashed border-[#e1e3e5] rounded-md p-4">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        onChange={handleFileSelect}
                                        className="hidden"
                                        id="file-upload"
                                        disabled={isSubmitting || isPublishing}
                                    />
                                    <label
                                        htmlFor="file-upload"
                                        className="flex flex-col items-center justify-center cursor-pointer"
                                    >
                                        <Upload className="w-8 h-8 text-tf-text-muted mb-2" />
                                        <span className="text-sm text-tf-text-muted">
                                            Click to upload files or drag and drop
                                        </span>
                                        <span className="text-xs text-tf-text-muted mt-1">
                                            PDF, TXT, DOCX, and other document formats
                                        </span>
                                    </label>
                                </div>

                                {uploadedFiles.length > 0 && (
                                    <div className="mt-4 space-y-2">
                                        {uploadedFiles.map((file, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center justify-between p-3 bg-tf-bg-muted rounded-md border border-tf-border"
                                            >
                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                    <FileText className="w-4 h-4 text-tf-text-muted flex-shrink-0" />
                                                    <span className="text-sm text-tf-text-sub truncate">
                                                        {file.filename}
                                                    </span>
                                                    {file.uploading && (
                                                        <Loader2 className="w-4 h-4 animate-spin text-primary ml-auto" />
                                                    )}
                                                    {file.error && (
                                                        <span className="text-xs text-red-600 ml-auto">
                                                            {file.error}
                                                        </span>
                                                    )}
                                                </div>
                                                {!file.uploading && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveFile(file.filename)}
                                                        className="ml-2 p-1 hover:bg-tf-feedback-error-bg rounded text-red-600"
                                                        disabled={isSubmitting || isPublishing}
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex justify-end gap-3 pt-4 border-t border-[#e1e3e5]">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => navigate(ROUTES.AGENTS.MY_AGENTS)}
                                disabled={isSubmitting || isPublishing}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="secondary"
                                disabled={isSubmitting || isPublishing || !formData.name.trim() || !formData.description.trim()}
                                icon={isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                            >
                                {isSubmitting ? 'Saving...' : agentId ? 'Update Draft' : 'Save Draft'}
                            </Button>
                            {agentId && (
                                <Button
                                    type="button"
                                    variant="primary"
                                    onClick={handlePublish}
                                    disabled={isSubmitting || isPublishing || !formData.name.trim() || !formData.description.trim()}
                                    icon={isPublishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                                >
                                    {isPublishing ? 'Publishing...' : 'Publish'}
                                </Button>
                            )}
                        </div>
                    </form>
                </CardSection>
            </Card>

            {/* Info Card */}
            <Card className="max-w-2xl">
                <CardSection>
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-tf-feedback-info-bg flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-sm font-semibold text-tf-text mb-2">What is an AI Agent?</h3>
                            <p className="text-sm text-tf-text-muted mb-3">
                                An AI agent is a virtual assistant that can perform specific tasks, answer questions, and help your customers or team. 
                                You can customize its behavior, knowledge, and capabilities according to your business needs.
                            </p>
                            <ul className="text-xs text-tf-text-muted space-y-1 list-disc list-inside">
                                <li>Answers customer questions</li>
                                <li>Provides information about products or services</li>
                                <li>Helps with automated tasks</li>
                                <li>Can integrate with your existing systems</li>
                            </ul>
                        </div>
                    </div>
                </CardSection>
            </Card>
        </div>
    )
}
